import { Button, DatePicker, Modal } from '@components/common';
import {
  FormInput,
  FormSelect,
  FormTextarea,
} from '@components/common/FormField';
import { Plus, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import {
  AccountRecord,
  JournalPostingScope,
} from '../../../../types/accounting';
import {
  calculateLineTotals,
  createEmptyJournalLine,
  JournalEntryFormState,
  JOURNAL_SOURCE_OPTIONS,
  POSTING_SCOPE_OPTIONS,
} from './journalForm';
import {
  getActiveJournalLines,
  validateJournalLines,
} from './journalValidation';

interface JournalEntryEditorModalProps {
  initial: JournalEntryFormState;
  accounts: AccountRecord[];
  outletName?: string | null;
  isSaving: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (form: JournalEntryFormState) => void;
}

export function JournalEntryEditorModal({
  initial,
  accounts,
  outletName,
  isSaving,
  title,
  onClose,
  onSubmit,
}: JournalEntryEditorModalProps) {
  const [form, setForm] = useState<JournalEntryFormState>(initial);
  const [error, setError] = useState<string | null>(null);

  const manualAccounts = useMemo(
    () =>
      accounts.filter(
        (account) => account.is_active && account.allows_manual_entries,
      ),
    [accounts],
  );

  const totals = useMemo(() => calculateLineTotals(form.lines), [form.lines]);
  const imbalance = Number((totals.debit - totals.credit).toFixed(2));

  const setField = <K extends keyof JournalEntryFormState>(
    key: K,
    value: JournalEntryFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  const setLineField = (
    index: number,
    key: keyof JournalEntryFormState['lines'][number],
    value: string,
  ) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, lineIndex) =>
        lineIndex === index ? { ...line, [key]: value } : line,
      ),
    }));
  };

  const addLine = () =>
    setForm((current) => ({
      ...current,
      lines: [...current.lines, createEmptyJournalLine()],
    }));

  const removeLine = (index: number) =>
    setForm((current) => ({
      ...current,
      lines:
        current.lines.length > 1
          ? current.lines.filter((_, lineIndex) => lineIndex !== index)
          : current.lines,
    }));

  const handlePostingScopeChange = (value: string) => {
    const nextScope = value as JournalPostingScope;

    setForm((current) => ({
      ...current,
      postingScope: nextScope,
      outletId:
        nextScope === 'outlet' ? current.outletId || initial.outletId : '',
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const activeLines = getActiveJournalLines(form.lines);
    const validationError = validateJournalLines(activeLines);

    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    onSubmit({ ...form, lines: activeLines });
  };

  return (
    <Modal isOpen onClose={onClose} title={title} size="xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <DatePicker
            label="Entry date"
            mode="single"
            value={form.entryDate}
            onChange={(value) => setField('entryDate', value)}
            placeholder="Select entry date"
          />
          <FormSelect
            label="Posting scope"
            required
            value={form.postingScope}
            onChange={(event) => handlePostingScopeChange(event.target.value)}
          >
            {POSTING_SCOPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
        </div>

        {form.postingScope === 'outlet' ? (
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            This journal will be posted against{' '}
            {outletName || 'the current outlet'}.
          </div>
        ) : null}

        <div className="grid gap-3 md:grid-cols-2">
          <FormInput
            label="Reference type"
            value={form.referenceType}
            onChange={(event) => setField('referenceType', event.target.value)}
            placeholder="e.g. manual_adjustment"
          />
          <FormInput
            label="Reference ID"
            value={form.referenceId}
            onChange={(event) => setField('referenceId', event.target.value)}
            placeholder="Optional source reference"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <FormSelect
            label="Source module"
            required
            value={form.sourceModule}
            onChange={(event) =>
              setField(
                'sourceModule',
                event.target.value as JournalEntryFormState['sourceModule'],
              )
            }
          >
            {JOURNAL_SOURCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
          <FormTextarea
            label="Memo"
            value={form.memo}
            onChange={(event) => setField('memo', event.target.value)}
            placeholder="Describe the purpose of this journal entry"
            rows={3}
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Journal lines
              </h3>
              <p className="text-sm text-gray-500">
                Add debit and credit lines using active accounts that allow
                manual entries.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={addLine}>
              <Plus size={14} />
              Add line
            </Button>
          </div>

          <div className="space-y-3">
            {form.lines.map((line, index) => (
              <div
                key={line.id || `line-${index}`}
                className="rounded-2xl border border-gray-200 p-3"
              >
                <div className="grid gap-4 lg:grid-cols-[2fr_2fr_1fr_1fr_auto]">
                  <FormSelect
                    label="Account"
                    value={line.accountId}
                    onChange={(event) =>
                      setLineField(index, 'accountId', event.target.value)
                    }
                  >
                    <option value="">Select account</option>
                    {manualAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.code} - {account.name}
                      </option>
                    ))}
                  </FormSelect>
                  <FormInput
                    label="Description"
                    value={line.description}
                    onChange={(event) =>
                      setLineField(index, 'description', event.target.value)
                    }
                    placeholder="Optional line note"
                  />
                  <FormInput
                    label="Debit"
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.debit}
                    onChange={(event) =>
                      setLineField(index, 'debit', event.target.value)
                    }
                    placeholder="0.00"
                  />
                  <FormInput
                    label="Credit"
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.credit}
                    onChange={(event) =>
                      setLineField(index, 'credit', event.target.value)
                    }
                    placeholder="0.00"
                  />
                  <div className="flex items-end">
                    <Button
                      variant="text"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => removeLine(index)}
                      disabled={form.lines.length === 1}
                    >
                      <Trash2 size={14} />
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <span className="font-medium text-gray-700">
              Debit: {totals.debit.toFixed(2)}
            </span>
            <span className="font-medium text-gray-700">
              Credit: {totals.credit.toFixed(2)}
            </span>
            <span
              className={`font-semibold ${
                imbalance === 0 ? 'text-emerald-700' : 'text-amber-700'
              }`}
            >
              {imbalance === 0
                ? 'Balanced'
                : `Difference: ${Math.abs(imbalance).toFixed(2)}`}
            </span>
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save draft
          </Button>
        </div>
      </form>
    </Modal>
  );
}
